// Help Center information architecture — ONE module, no dependencies.
//
// Two jobs, both pure so /help and /resources can each import it without dragging the other
// library's bundle in (this file deliberately imports NOTHING — the callers pass the real data in):
//
//   1. AUTO-ARRANGEMENT. The public Help Center groups its articles by category. The category
//      ORDER used to be whatever `/help/public/categories` returned, which is alphabetical — so
//      "AI Assistant" led the page and "Getting Started" sat ninth. `HELP_CATEGORY_ORDER` below is
//      a deliberate buyer/user journey (start → move in → money → trust → run the place → teach →
//      platform depth), mirroring how RESOURCE_TOPICS is ordered in resources/resourceLibrary.js.
//      A category NOT in the list still renders — it sorts after the known ones, alphabetically
//      among its peers — so a newly authored category can never vanish, and never silently lands
//      mid-journey by accident. (helpTaxonomy.test.js pins both halves of that rule.)
//
//   2. CROSS-DISCOVERY. /help (platform-admin-owned knowledge base, served from the CMS) and
//      /resources (code-authored editorial library) are two governed systems that must stay
//      separate but must not hide each other. `helpCategoriesForResource` derives, for one
//      resource article, which Help Center categories cover the same subject — from data the
//      article ALREADY declares (its `moduleLinks` and its `topicKey`), never a per-article list
//      that would need updating every time a guide ships.
//
// The bridge below is a TAXONOMY map (help category → the product areas it documents), not an
// article map: adding a resource guide, or a knowledge-base article, needs no edit here.

/**
 * The journey order for the grouped /help index. Every entry is a live public category as returned
 * by GET /help/public/categories; the order is the reading order, not the API's alphabetical one.
 */
export const HELP_CATEGORY_ORDER = Object.freeze([
  // Start here
  "Getting Started",
  "Onboarding",
  "Data Migration",
  // Money
  "Fees & Finance",
  "Payments",
  "Billing & Plans",
  // Trust
  "Compliance",
  "Security",
  // Run the place
  "Communication",
  "Operations",
  "Transport",
  // Teach
  "Exams",
  "Report Cards",
  // Platform depth
  "AI Assistant",
  "Automation",
  "White-label",
  "Multi-tenancy",
  // Short answers — kept out of the grouped index (see HELP_INDEX_EXCLUDED_CATEGORIES) because
  // they restate the guides' "In short" summaries; reachable via the chip and search.
  "FAQ",
]);

/** Categories the UNFILTERED grouped index leaves out. Still reachable by chip and by search. */
export const HELP_INDEX_EXCLUDED_CATEGORIES = Object.freeze(["FAQ"]);

const orderIndex = (category) => {
  const i = HELP_CATEGORY_ORDER.indexOf(category);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

/**
 * Sort category names into the journey order. Unknown categories keep their place at the END, in
 * alphabetical order among themselves — a deliberate, visible fallback rather than a silent drop.
 */
export function orderHelpCategories(names) {
  return [...new Set((names || []).filter(Boolean))].sort((a, b) => {
    const ai = orderIndex(a);
    const bi = orderIndex(b);
    if (ai !== bi) return ai - bi;
    return String(a).localeCompare(String(b));
  });
}

/**
 * Group a flat article list into ordered [{ category, articles }] sections, preserving the API's
 * within-category order (it sorts by `order` then `title`).
 *
 * @param {Array<{category?: string}>} articles
 * @param {{exclude?: string[]}} [options] categories to leave out entirely
 */
export function groupHelpArticles(articles, options = {}) {
  const exclude = new Set(options.exclude || []);
  const byCategory = new Map();
  for (const article of articles || []) {
    const category = article?.category || "General";
    if (exclude.has(category)) continue;
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category).push(article);
  }
  return orderHelpCategories([...byCategory.keys()]).map((category) => ({
    category,
    articles: byCategory.get(category),
  }));
}

// ---------------------------------------------------------------------------
// The cross-discovery bridge
// ---------------------------------------------------------------------------
//
// One row per help category that a resource guide can plausibly hand off to, describing the
// product surface that category documents:
//
//   modules — module-catalogue keys (landingparts/moduleCatalog) the category is ABOUT.
//   groups  — whole module-catalogue group keys, when the category covers the area wholesale.
//   topics  — resourceLibrary RESOURCE_TOPICS keys whose subject is this category.
//
// Rows are deliberately absent where no public help article covers the area (attendance,
// timetabling, payroll and reporting have no public category today) — a missing row renders the
// honest generic Help Center link instead of a link that lands on an empty filter.
// helpTaxonomy.test.js asserts every key here is real on both sides.
export const HELP_CATEGORY_PRODUCT_MAP = Object.freeze([
  { category: "Onboarding", topics: ["implementation"], modules: ["institute-settings"], groups: [] },
  { category: "Data Migration", topics: ["implementation"], modules: ["migration-copilot"], groups: [] },
  { category: "Fees & Finance", topics: ["fees"], modules: [], groups: ["fees"] },
  {
    category: "Payments",
    topics: [],
    modules: ["fee-collection", "online-payment", "payment-gateway", "receipt-generation", "refund-management"],
    groups: [],
  },
  { category: "Billing & Plans", topics: ["buying"], modules: [], groups: [] },
  { category: "Compliance", topics: ["compliance"], modules: ["compliance-ai"], groups: [] },
  // Access control only — staff-management is an HR record surface, not the security boundary.
  { category: "Security", topics: [], modules: ["role-and-permission", "user-management"], groups: [] },
  { category: "Communication", topics: [], modules: [], groups: ["comms"] },
  {
    category: "Operations",
    topics: [],
    modules: [
      "hostel-management",
      "library-management",
      "inventory-management",
      "front-office",
      "visitor-management",
      "asset-management",
      "task-management",
      "biometric-attendance",
    ],
    groups: [],
  },
  { category: "Transport", topics: [], modules: ["transport-management", "transport-fees", "transport-reports"], groups: [] },
  { category: "Exams", topics: [], modules: ["exam-scheduling", "digital-gradebook", "exam-reports", "subject-management"], groups: [] },
  { category: "Report Cards", topics: [], modules: ["result-publishing", "report-card-ai"], groups: [] },
  { category: "AI Assistant", topics: [], modules: [], groups: ["aira"] },
  {
    category: "Automation",
    topics: [],
    modules: ["automations", "automated-fee-reminders", "fee-reminder", "approval-workflow"],
    groups: [],
  },
  { category: "Multi-tenancy", topics: [], modules: ["branch-management", "branch-wise-reports"], groups: [] },
]);

// Signal weights. A declared TOPIC and a named MODULE are equally specific statements of subject;
// a whole-GROUP match is one step broader, so it ranks below them. Each signal counts ONCE per
// category — otherwise a guide that links four fee modules would bury a more precise single hit.
const SIGNAL_WEIGHTS = Object.freeze({ topic: 3, module: 3, group: 2 });

/** How many Help Center categories one resource article may hand off to. */
export const MAX_HELP_CATEGORIES_PER_RESOURCE = 3;

/**
 * The Help Center categories that cover the same subject as one resource article.
 *
 * Derived — never authored per article — from the article's own declarations:
 *   • `moduleLinks`  → the module keys, and (via `groupOf`) the catalogue group each belongs to
 *   • `topicKey`     → the library topic it was filed under
 *
 * @param {{moduleLinks?: string[], topicKey?: string}} article
 * @param {(moduleKey: string) => (string|undefined)} groupOf resolves a module key to its catalogue
 *   group key. The caller supplies it (usually `(k) => moduleByKey[k]?.group`) so this module stays
 *   dependency-free and cheap to import from the Help Center bundle.
 * @param {number} [limit]
 * @returns {string[]} category names, most relevant first, at most `limit`
 */
export function helpCategoriesForResource(article, groupOf, limit = MAX_HELP_CATEGORIES_PER_RESOURCE) {
  if (!article) return [];
  const moduleKeys = new Set(article.moduleLinks || []);
  const groupKeys = new Set();
  if (typeof groupOf === "function") {
    for (const key of moduleKeys) {
      const group = groupOf(key);
      if (group) groupKeys.add(group);
    }
  }
  const topicKey = article.topicKey;

  const scored = [];
  for (const row of HELP_CATEGORY_PRODUCT_MAP) {
    let score = 0;
    if (topicKey && (row.topics || []).includes(topicKey)) score += SIGNAL_WEIGHTS.topic;
    if ((row.modules || []).some((key) => moduleKeys.has(key))) score += SIGNAL_WEIGHTS.module;
    if ((row.groups || []).some((key) => groupKeys.has(key))) score += SIGNAL_WEIGHTS.group;
    if (score > 0) scored.push({ category: row.category, score });
  }
  scored.sort((a, b) => (b.score - a.score) || (orderIndex(a.category) - orderIndex(b.category)));
  return scored.slice(0, Math.max(0, limit)).map((row) => row.category);
}

/** The deep link to the Help Center filtered to one category. */
export const helpCategoryPath = (category) => `/help?category=${encodeURIComponent(category)}`;

/** A stable, testid-safe token for a category name ("Fees & Finance" → "fees-finance"). */
export const helpCategorySlug = (category) =>
  String(category || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
